import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Badge,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Pagination,
  PaginationItem,
  PaginationLink,
  Collapse,
} from "reactstrap";
import classnames from "classnames";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import axiosInstance from "../../../helpers/axiosInstance";

const UserDetails = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState("1");
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [orderPagination, setOrderPagination] = useState({
    page: 1,
    limit: 10,
    totalOrders: 0,
    totalPages: 0,
    hasMore: false,
  });

  useEffect(() => {
    fetchUserData();
  }, [id]);

  useEffect(() => {
    if (activeTab === "1") {
      fetchUserOrders();
    }
  }, [id, activeTab, orderPagination.page]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/v1/admin/users/${id}`);
      setUserData(response.data.user);
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserOrders = async () => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/admin/users/${id}/orders`,
        {
          params: {
            page: orderPagination.page,
            limit: orderPagination.limit,
          },
        }
      );
      setOrders(response.data.orders);
      setOrderPagination(response.data.pagination);
    } catch (error) {
      console.error("Error fetching user orders:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not Available";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
      case "completed":
        return "success";
      case "processing":
        return "info";
      case "cancelled":
        return "danger";
      case "pending":
        return "warning";
      default:
        return "secondary";
    }
  };

  const toggleOrderDetails = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="User Details"
          breadcrumbItems={[
            { title: "Ecommerce", link: "#" },
            { title: "User Details", link: "#" },
          ]}
        />

        <Row>
          <Col xl={4}>
            <Card>
              <CardBody>
                <div className="text-center">
                  <div className="avatar-xl mx-auto mb-4">
                    <div className="avatar-title bg-primary-subtle text-primary display-4 rounded-circle">
                      {userData?.name?.charAt(0).toUpperCase() || "U"}
                    </div>
                  </div>
                  <h5 className="font-size-16 mb-1">{userData?.name}</h5>
                  <p className="text-muted mb-2">{userData?.role}</p>
                </div>

                <div className="mt-4">
                  <Row>
                    <Col xs={6}>
                      <h5 className="font-size-16">Contact Info</h5>
                      <p className="text-muted mb-1">
                        <i className="ri-mail-line me-2"></i> {userData?.email}
                      </p>
                      <p className="text-muted">
                        <i className="ri-phone-line me-2"></i> {userData?.phone}
                      </p>
                    </Col>
                    <Col xs={6}>
                      <h5 className="font-size-16">Verification</h5>
                      <div>
                        <Badge
                          color={
                            userData?.verification?.email?.verified
                              ? "success"
                              : "warning"
                          }
                          className="me-2"
                        >
                          Email{" "}
                          {userData?.verification?.email?.verified ? "✓" : "✗"}
                        </Badge>
                        <Badge
                          color={
                            userData?.verification?.phone?.verified
                              ? "success"
                              : "warning"
                          }
                        >
                          Phone{" "}
                          {userData?.verification?.phone?.verified ? "✓" : "✗"}
                        </Badge>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* <div className="mt-4">
                  <h5 className="font-size-16">Wallet Balance</h5>
                  <h3 className="text-primary">
                    {formatCurrency(userData?.wallet?.balance)}
                  </h3>
                </div> */}

                <div className="mt-4">
                  <h5 className="font-size-16">Referral Info</h5>
                  <p className="mb-2">
                    <strong>Code:</strong> {userData?.referral?.code}
                  </p>
                  {userData?.referral?.referredBy && (
                    <p className="mb-0">
                      <strong>Referred By:</strong>{" "}
                      {userData?.referral?.referredBy}
                    </p>
                  )}
                </div>

                <div className="mt-4">
                  <h5 className="font-size-16">Account Details</h5>
                  <div className="table-responsive">
                    <Table className="table-nowrap mb-0">
                      <tbody>
                        <tr>
                          <th scope="row">Age :</th>
                          <td>{userData?.age || "N/A"}</td>
                        </tr>
                        <tr>
                          <th scope="row">Joined :</th>
                          <td>{formatDate(userData?.createdAt)}</td>
                        </tr>
                        <tr>
                          <th scope="row">Email Verified :</th>
                          <td>
                            {formatDate(
                              userData?.verification?.email?.verifiedAt
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">Phone Verified :</th>
                          <td>
                            {formatDate(
                              userData?.verification?.phone?.verifiedAt
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl={8}>
            <Card>
              <CardBody>
                <Nav tabs>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "1" })}
                      onClick={() => setActiveTab("1")}
                    >
                      Orders
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "2" })}
                      onClick={() => setActiveTab("2")}
                    >
                      Recent Transactions
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab} className="pt-4">
                  <TabPane tabId="1">
                    <div className="table-responsive">
                      <Table className="table-centered table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: "20px" }}></th>
                            <th>Order ID</th>
                            <th>Date</th>
                            <th>Amount</th>
                            <th>Payment</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders.map((order) => (
                            <React.Fragment key={order.id}>
                              <tr>
                                <td>
                                  <button
                                    className="btn btn-link p-0"
                                    onClick={() => toggleOrderDetails(order.id)}
                                  >
                                    <i
                                      className={`mdi mdi-chevron-${
                                        expandedOrder === order.id
                                          ? "up"
                                          : "down"
                                      }`}
                                    />
                                  </button>
                                </td>
                                <td>
                                  <a
                                    href={`/order-details/${order.id}`}
                                    className="text-dark fw-medium"
                                  >
                                    #{order.id}
                                  </a>
                                </td>
                                <td>{formatDate(order.orderedAt)}</td>
                                <td>{formatCurrency(order.totalAmount)}</td>
                                <td>
                                  <div>
                                    <Badge
                                      color={getStatusColor(
                                        order.paymentStatus
                                      )}
                                      className="me-1"
                                    >
                                      {order.paymentStatus}
                                    </Badge>
                                    <small className="text-muted">
                                      {order.paymentMethod}
                                    </small>
                                  </div>
                                </td>
                                <td>
                                  <Badge color={getStatusColor(order.status)}>
                                    {order.status}
                                  </Badge>
                                </td>
                              </tr>
                              <tr>
                                <td colSpan="6" className="p-0">
                                  <Collapse isOpen={expandedOrder === order.id}>
                                    <div className="p-3 bg-light">
                                      <Table className="table-sm">
                                        <thead>
                                          <tr>
                                            <th>Product</th>
                                            <th>Price</th>
                                            <th>Quantity</th>
                                            <th>Total</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {order.items.map((item, index) => (
                                            <tr key={index}>
                                              <td>
                                                <div className="d-flex align-items-center">
                                                  {item.product.image && (
                                                    <img
                                                      src={item.product.image.startsWith("https") ? item.product.image : `${process.env.REACT_APP_BASEURL}${item.product.image}`}
                                                      alt={item.product.name}
                                                      className="rounded me-2"
                                                      style={{
                                                        width: "40px",
                                                        height: "40px",
                                                        objectFit: "cover",
                                                      }}
                                                    />
                                                  )}
                                                  <div>
                                                    <h6 className="mb-0">
                                                      {item.product.name}
                                                    </h6>
                                                    <small className="text-muted">
                                                      #{item.product.id}
                                                    </small>
                                                  </div>
                                                </div>
                                              </td>
                                              <td>
                                                {formatCurrency(
                                                  item.product.price
                                                )}
                                              </td>
                                              <td>{item.quantity}</td>
                                              <td>
                                                {formatCurrency(
                                                  item.product.price *
                                                    item.quantity
                                                )}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </Table>

                                      <div className="mt-3">
                                        <h6 className="mb-2">
                                          Shipping Address
                                        </h6>
                                        <p className="mb-0">
                                          {order.shippingAddress.street}
                                          <br />
                                          {order?.shippingAddress?.city},{" "}
                                          {order?.shippingAddress?.state}
                                          <br />
                                          {order?.shippingAddress?.country} -{" "}
                                          {order?.shippingAddress?.postalCode}
                                        </p>
                                      </div>
                                    </div>
                                  </Collapse>
                                </td>
                              </tr>
                            </React.Fragment>
                          ))}
                          {orders.length === 0 && (
                            <tr>
                              <td colSpan="6" className="text-center">
                                No orders found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>

                      {orderPagination.totalPages > 1 && (
                        <div className="d-flex justify-content-end mt-3">
                          <Pagination>
                            <PaginationItem
                              disabled={orderPagination.page === 1}
                            >
                              <PaginationLink
                                previous
                                onClick={() =>
                                  setOrderPagination((prev) => ({
                                    ...prev,
                                    page: prev.page - 1,
                                  }))
                                }
                              />
                            </PaginationItem>

                            {[...Array(orderPagination.totalPages)].map(
                              (_, index) => (
                                <PaginationItem
                                  key={index + 1}
                                  active={orderPagination.page === index + 1}
                                >
                                  <PaginationLink
                                    onClick={() =>
                                      setOrderPagination((prev) => ({
                                        ...prev,
                                        page: index + 1,
                                      }))
                                    }
                                  >
                                    {index + 1}
                                  </PaginationLink>
                                </PaginationItem>
                              )
                            )}

                            <PaginationItem disabled={!orderPagination.hasMore}>
                              <PaginationLink
                                next
                                onClick={() =>
                                  setOrderPagination((prev) => ({
                                    ...prev,
                                    page: prev.page + 1,
                                  }))
                                }
                              />
                            </PaginationItem>
                          </Pagination>
                        </div>
                      )}
                    </div>
                  </TabPane>

                  <TabPane tabId="2">
                    <div className="table-responsive">
                      <Table className="table-centered table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Description</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userData?.wallet?.recentTransactions?.map(
                            (transaction, index) => (
                              <tr key={index}>
                                <td>{formatDate(transaction.createdAt)}</td>
                                <td>
                                  <Badge
                                    color={
                                      transaction.type === "credit"
                                        ? "success"
                                        : "danger"
                                    }
                                  >
                                    {transaction.type}
                                  </Badge>
                                </td>
                                <td>{transaction.description}</td>
                                <td>
                                  <span
                                    className={`text-${
                                      transaction.type === "credit"
                                        ? "success"
                                        : "danger"
                                    }`}
                                  >
                                    {transaction.type === "credit" ? "+" : "-"}
                                    {formatCurrency(transaction.amount)}
                                  </span>
                                </td>
                              </tr>
                            )
                          )}
                          {!userData?.wallet?.recentTransactions?.length && (
                            <tr>
                              <td colSpan="4" className="text-center">
                                No recent transactions found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default UserDetails;
